// src/components/DraggableWindow/index.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { combineThemeClasses } from '../../utils/theme';
import WindowControls from './WindowControls';

const DraggableWindow = ({
  children,
  title = 'Untitled',
  windowId,
  isResizable = true,
  onClose,
  onMinimize,
  onFocus,
  position = { x: 100, y: 100 },
  size = { width: 800, height: 600 },
  zIndex = 1,
  onPositionChange,
  onSizeChange,
  metadata,
  applyLayout, // Passed from AppLauncher for layout
  theme = 'dark'
}) => {
  const windowRef = useRef(null);

  const posX = isNaN(position.x) ? 100 : position.x;
  const posY = isNaN(position.y) ? 100 : position.y;
  const w = isNaN(size.width) ? 800 : size.width;
  const h = isNaN(size.height) ? 600 : size.height;

  const [isDragging, setIsDragging] = useState(false);
  const dragStartMouse = useRef({ x: 0, y: 0 });
  const dragStartWindow = useRef({ x: posX, y: posY });

  const headerRef = useRef(null);

  const handlePointerDownHeader = useCallback((e) => {
    if (e.target.closest('.window-controls') || e.target.closest('.resize-handle')) return;
    onFocus?.();
    dragStartWindow.current = { x: posX, y: posY };
    dragStartMouse.current = { x: e.clientX, y: e.clientY };
    // Capture pointer to prevent iframes from stealing events
    e.target.setPointerCapture?.(e.pointerId);
    setIsDragging(true);
  }, [onFocus, posX, posY]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const dx = (e.clientX - dragStartMouse.current.x);
    const dy = (e.clientY - dragStartMouse.current.y);
    onPositionChange?.({ x: dragStartWindow.current.x + dx, y: dragStartWindow.current.y + dy });
  }, [isDragging, onPositionChange]);

  const handleDragEnd = useCallback((e) => {
    // Release pointer capture
    e?.target?.releasePointerCapture?.(e.pointerId);
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handleDragMove);
      window.addEventListener('pointerup', handleDragEnd);
    } else {
      window.removeEventListener('pointermove', handleDragMove);
      window.removeEventListener('pointerup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('pointermove', handleDragMove);
      window.removeEventListener('pointerup', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const resizeStartMouse = useRef({ x: 0, y: 0 });
  const resizeStartWindow = useRef({ x: posX, y: posY, width: w, height: h });

  const handleResizeStart = useCallback((e, direction) => {
    e.stopPropagation();
    onFocus?.();
    resizeStartMouse.current = { x: e.clientX, y: e.clientY };
    resizeStartWindow.current = { x: posX, y: posY, width: w, height: h };
    // Capture pointer to prevent iframes from stealing events
    e.target.setPointerCapture?.(e.pointerId);
    setResizeDirection(direction);
    setIsResizing(true);
  }, [onFocus, posX, posY, w, h]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeDirection) return;
    const dx = e.clientX - resizeStartMouse.current.x;
    const dy = e.clientY - resizeStartMouse.current.y;

    let newWidth = resizeStartWindow.current.width;
    let newHeight = resizeStartWindow.current.height;
    let newX = resizeStartWindow.current.x;
    let newY = resizeStartWindow.current.y;

    const minW = metadata?.minSize?.width || 100;
    const minH = metadata?.minSize?.height || 100;

    if (resizeDirection.includes('e')) {
      newWidth = resizeStartWindow.current.width + dx;
      if (newWidth < minW) newWidth = minW;
    }
    if (resizeDirection.includes('s')) {
      newHeight = resizeStartWindow.current.height + dy;
      if (newHeight < minH) newHeight = minH;
    }
    if (resizeDirection.includes('w')) {
      const potentialWidth = resizeStartWindow.current.width - dx;
      if (potentialWidth >= minW) {
        newWidth = potentialWidth;
        newX = resizeStartWindow.current.x + dx;
      } else {
        newWidth = minW;
        newX = resizeStartWindow.current.x + (resizeStartWindow.current.width - minW);
      }
    }
    if (resizeDirection.includes('n')) {
      const potentialHeight = resizeStartWindow.current.height - dy;
      if (potentialHeight >= minH) {
        newHeight = potentialHeight;
        newY = resizeStartWindow.current.y + dy;
      } else {
        newHeight = minH;
        newY = resizeStartWindow.current.y + (resizeStartWindow.current.height - minH);
      }
    }

    onSizeChange?.({ width: newWidth, height: newHeight });
    onPositionChange?.({ x: newX, y: newY });
  }, [isResizing, resizeDirection, metadata, onSizeChange, onPositionChange]);

  const handleResizeEnd = useCallback((e) => {
    // Release pointer capture
    e?.target?.releasePointerCapture?.(e.pointerId);
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('pointermove', handleResizeMove);
      window.addEventListener('pointerup', handleResizeEnd);
    } else {
      window.removeEventListener('pointermove', handleResizeMove);
      window.removeEventListener('pointerup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('pointermove', handleResizeMove);
      window.removeEventListener('pointerup', handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const windowBaseClass = combineThemeClasses(theme, ['window.base']);
  const windowDraggingClass = isDragging
    ? combineThemeClasses(theme, ['window.dragging.active', 'window.dragging.transition'])
    : combineThemeClasses(theme, ['window.dragging.inactive', 'window.dragging.transition']);

  return (
    <div
      ref={windowRef}
      style={{
        position: 'absolute',
        left: posX,
        top: posY,
        width: w,
        height: h,
        zIndex,
        minWidth: '50px',
        minHeight: '50px'
      }}
      className={`${windowBaseClass} ${windowDraggingClass}`}
      onClick={onFocus}
    >
      <div
        ref={headerRef}
        onPointerDown={handlePointerDownHeader}
        className={combineThemeClasses(theme, ['window.header.base'])}
        style={{ cursor: 'move', touchAction: 'none' }}
      >
        <span className={combineThemeClasses(theme, ['window.header.title'])}>
          {title}
        </span>
        <WindowControls
          onMinimize={onMinimize}
          onClose={onClose}
          onMaximize={() => applyLayout && applyLayout('fill')}
          onLayoutSelect={(layoutName) => applyLayout && applyLayout(layoutName)}
        />
      </div>

      <div
        className={`${combineThemeClasses(theme, ['window.content.base'])} h-[calc(100%-24px)] md:h-[calc(100%-32px)]`}
      >
        {children}
      </div>

      {isResizable && (
        <>
          <div className="absolute top-0 left-0 w-2 h-2 cursor-nw-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 'nw')} />
          <div className="absolute top-0 right-0 w-2 h-2 cursor-ne-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 'ne')} />
          <div className="absolute bottom-0 left-0 w-2 h-2 cursor-sw-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 'sw')} />
          <div className="absolute bottom-0 right-0 w-2 h-2 cursor-se-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 'se')} />

          <div className="absolute top-2 left-0 right-0 h-2 cursor-n-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 'n')} />
          <div className="absolute bottom-0 left-2 right-2 h-2 cursor-s-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 's')} />
          <div className="absolute left-0 top-2 bottom-2 w-2 cursor-w-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 'w')} />
          <div className="absolute right-0 top-2 bottom-2 w-2 cursor-e-resize resize-handle z-50"
            style={{ touchAction: 'none' }}
            onPointerDown={(e) => handleResizeStart(e, 'e')} />
        </>
      )}
    </div>
  );
};

export default DraggableWindow;
